import { useState, useEffect, SetStateAction, Dispatch } from 'react'
import { ISchedulerStateProps } from '../container-modal/initialData'
import useFetch from '../../../../../global/hooks/useFetchHook'
import { getServices } from '../../../../../global/api'
import { IService } from '../../../../../../../app/entities/Service'
import { ILocationService } from '../../../../../../../app/entities/LocationService'
import { fetchData } from '../../../../../global/utils/fetch'
import { getLocationServiceByServiceForOrder } from './api'
import { IOrderService } from '../../../../../../../app/entities/OrderService'
import { DeepPartial } from 'typeorm'

interface IChangeServiceCustomHookProps {
  schedulerState: ISchedulerStateProps
  setSchedulerState: Dispatch<SetStateAction<ISchedulerStateProps>>
}

const getLocationService = async (
  schedulerState: ISchedulerStateProps,
  setNewOrderService: Dispatch<SetStateAction<DeepPartial<IOrderService>>>,
) => {
  const mostRecentLocationServiceForLocation =
    await fetchData<ILocationService>(
      getLocationServiceByServiceForOrder(schedulerState),
    )
  setNewOrderService((prevState: DeepPartial<IOrderService>) => ({
    ...prevState,
    price: mostRecentLocationServiceForLocation.price,
  }))
  return
}

function useDetectChangeOrderService(props: IChangeServiceCustomHookProps) {
  //This custom hook is used to detect if user changes selected service / or disposal qty and provides the body for a new orderService
  const { data: servicesData } = useFetch<IService[]>(getServices())
  const { schedulerState } = props

  const {
    servicePriceOverride,
    service: serviceObjectId,
    tiresCombination,
    disposalQty,
    orderVehicleId,
  } = schedulerState

  const [originalService, setOriginalService] = useState(serviceObjectId)
  const [serviceHasChanged, setServiceHasChanged] = useState(false)
  const [originalDisposalQty, setOriginalDisposalQty] = useState(disposalQty)

  const [originalTireQuantity, setOriginalTireQuantity] = useState<number>(
    Number(tiresCombination.frontCount) + Number(tiresCombination.rearCount),
  )

  const [disposalQtyHasChanged, setDisposalQtyHasChanged] = useState(false)
  const [tireQuantityHasChanged, setTireQuantityHasChanged] = useState(false)
  const [newOrderService, setNewOrderService] = useState<
    DeepPartial<IOrderService>
  >({
    label: '',
    orderVehicle: {
      objectId: '',
    },
    price: 0,
    quantity: 0,
    service: {
      defaultPrice: 0,
      objectId: '',
    },
  })

  useEffect(() => {
    // Initialize originalService only once
    if (originalService === null || originalService === undefined) {
      setOriginalService(serviceObjectId)
    }
    // Initialize originalDisposalQty only once
    if (originalDisposalQty === null || originalDisposalQty === undefined) {
      setOriginalDisposalQty(disposalQty)
    }
    //If original disposal qty changes, compare it to the original value
    const disposalQtyHasChanged = disposalQty !== originalDisposalQty
    setDisposalQtyHasChanged(disposalQtyHasChanged)
    // If the service value changes, compare it to the original value

    const tireQuantityHasChanged =
      originalTireQuantity !==
      Number(tiresCombination.frontCount) + Number(tiresCombination.rearCount)

    if (tireQuantityHasChanged) {
      setOriginalTireQuantity(
        Number(tiresCombination.frontCount) +
          Number(tiresCombination.rearCount),
      )

      setTireQuantityHasChanged(tireQuantityHasChanged)
    }

    const serviceHasChanged =
      serviceObjectId !== originalService || (servicePriceOverride ?? 0)

    if (serviceHasChanged) {
      setServiceHasChanged(true)
      const newSelectedService = servicesData?.find(
        (service: IService) => service.objectId === serviceObjectId,
      )

      //spread operator in next two due to asnyc call in location services
      //GetLocationService adds the price to the newOrderService and setNewOrderService adds additional information
      getLocationService(schedulerState, setNewOrderService)

      setNewOrderService((prevState) => ({
        ...prevState,
        label: newSelectedService?.name ?? '',
        orderVehicle: {
          objectId: orderVehicleId,
        },
        priceOverride: servicePriceOverride,
        quantity:
          Number(tiresCombination.frontCount) +
          Number(tiresCombination.rearCount),
        service: {
          defaultPrice: newSelectedService?.defaultPrice ?? 0,
          objectId: serviceObjectId,
        },
      }))
    } else {
      setServiceHasChanged(false)
    }
  }, [
    schedulerState.service,
    originalService,
    schedulerState.disposalQty,
    originalDisposalQty,
    servicePriceOverride,
    servicesData,
    schedulerState.tiresCombination,
  ])

  return {
    serviceHasChanged,
    newOrderService,
    disposalQtyHasChanged,
    tireQuantityHasChanged,
  }
}

export default useDetectChangeOrderService
